/**
 * Feature flags for controlling migration paths
 *
 * Usage:
 * - Set via VITE_FEATURE_FLAGS env var (comma-separated)
 * - Or localStorage 'featureFlags' (JSON object)
 * - Development default: all flags disabled (conservative rollout)
 *
 * Strangler Pattern:
 * These flags enable gradual migration from legacy implementations
 * to clean architecture without disrupting production.
 */

export interface FeatureFlags {
  /** Use new clean architecture for playlist fetching */
  useCleanPlaylistFetch: boolean;
  /** Use new clean architecture for station fetching */
  useCleanStationFetch: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  useCleanPlaylistFetch: false,
  useCleanStationFetch: false,
};

/**
 * Parse flags from environment variable
 * Format: "useCleanPlaylistFetch,useCleanStationFetch"
 */
function parseEnvFlags(): Partial<FeatureFlags> {
  const envFlags = import.meta.env.VITE_FEATURE_FLAGS;
  if (!envFlags) return {};

  const flags: Partial<FeatureFlags> = {};
  const flagList = envFlags.split(',').map((f: string) => f.trim());

  if (flagList.includes('useCleanPlaylistFetch')) flags.useCleanPlaylistFetch = true;
  if (flagList.includes('useCleanStationFetch')) flags.useCleanStationFetch = true;

  return flags;
}

/**
 * Parse flags from localStorage (for testing and development)
 * Format: { "useCleanPlaylistFetch": true, "useCleanStationFetch": false }
 */
function parseLocalFlags(): Partial<FeatureFlags> {
  try {
    const stored = localStorage.getItem('featureFlags');
    if (!stored) return {};
    const flags = JSON.parse(stored);
    return flags as Partial<FeatureFlags>;
  } catch {
    return {};
  }
}

/**
 * Get current feature flags
 * Priority: localStorage > env > defaults
 *
 * This allows:
 * - Production to use env defaults
 * - Development to override via localStorage
 * - Testing to set specific flags per test
 */
export function getFeatureFlags(): FeatureFlags {
  return {
    ...DEFAULT_FLAGS,
    ...parseEnvFlags(),
    ...parseLocalFlags(),
  };
}

// ============================================================================
// Convenience Accessors
// ============================================================================

/**
 * Check if clean architecture playlist fetch is enabled
 */
export const isCleanPlaylistFetchEnabled = (): boolean =>
  getFeatureFlags().useCleanPlaylistFetch;

/**
 * Check if clean architecture station fetch is enabled
 */
export const isCleanStationFetchEnabled = (): boolean =>
  getFeatureFlags().useCleanStationFetch;

// ============================================================================
// Testing Utilities
// ============================================================================

/**
 * Set a feature flag programmatically (for testing)
 * Persists to localStorage
 *
 * @param flag - The flag key to set
 * @param value - The value to set
 */
export function setFeatureFlag(flag: keyof FeatureFlags, value: boolean): void {
  const current = parseLocalFlags();
  localStorage.setItem('featureFlags', JSON.stringify({ ...current, [flag]: value }));
}

/**
 * Set multiple feature flags at once
 *
 * @param flags - Partial flags object
 */
export function setFeatureFlags(flags: Partial<FeatureFlags>): void {
  const current = parseLocalFlags();
  localStorage.setItem('featureFlags', JSON.stringify({ ...current, ...flags }));
}

/**
 * Clear all feature flags from localStorage
 * Flags will revert to env > defaults
 */
export function clearFeatureFlags(): void {
  localStorage.removeItem('featureFlags');
}

/**
 * Reset a specific flag to its default
 *
 * @param flag - The flag key to reset
 */
export function resetFeatureFlag(flag: keyof FeatureFlags): void {
  const current = parseLocalFlags();
  delete current[flag];
  if (Object.keys(current).length === 0) {
    localStorage.removeItem('featureFlags');
  } else {
    localStorage.setItem('featureFlags', JSON.stringify(current));
  }
}
